import { AppMode, AppSettings, UserRoleTypes } from '@types';
import { createSelector } from '@reduxjs/toolkit';
import { ClientExtendedRepresentationDetailed } from '@services/df/clients';
import { defineUserPermissions } from '@modules/user';
import { selectAppSettings } from '@modules/app';
import { selectIsAuthenticated } from '@modules/auth';
import {
    getCompanyNameByPath,
    matchesIqtRoutes,
    matchesShortIqtRoutes,
    findNonPersistedRoutes,
} from './routes/matchers';

function getAppMode(pathname: string) {
    if (matchesIqtRoutes(pathname)) {
        return AppMode.Iqt;
    }

    if (matchesShortIqtRoutes(pathname)) {
        return AppMode.ShortIqt;
    }

    return AppMode.EndUser;
}

export const AppSession = Object.freeze(
    (() => {
        const initPathname = window.location.pathname;
        const mode = getAppMode(initPathname);
        const endUserModeOn = mode === AppMode.EndUser;
        const iqtModeOn = mode === AppMode.Iqt;
        const shortIqtModeOn = mode === AppMode.ShortIqt;

        return {
            allowPersist: !Boolean(findNonPersistedRoutes(initPathname)),
            get companyName() {
                return getCompanyNameByPath();
            },
            iFrame: !(window === window.parent || window.opener),
            intentionToOpenIqt: iqtModeOn || shortIqtModeOn,
            iqtModeOn,
            shortIqtModeOn,
            endUserModeOn,
            initPathname,
            mode,
        };
    })(),
);

global.AppSession = AppSession;

interface HasIqtModeOnProps {
    settings: AppSettings;
    isAuthenticated: boolean;
    user?: ClientExtendedRepresentationDetailed;
}

//----- It partially copies functionality of defineUserPermissions, but we can't import this function here -----//
export const hasIqtModeOn = ({ settings, isAuthenticated, user }: HasIqtModeOnProps) => {
    const userRoles = user?.roles || settings.roles;
    const userIsCompanyOwner = userRoles.includes(UserRoleTypes.CompanyOwner);
    const userIsSalesman = userRoles.includes(UserRoleTypes.Salesman);
    const iqtAllowed =
        isAuthenticated && (userIsCompanyOwner || userIsSalesman) && (!AppSession.iFrame || AppSession.shortIqtModeOn);
    return iqtAllowed && AppSession.intentionToOpenIqt;
};
